import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  ensureEmptySwimlanes,
  releaseHasVisibleProjects
} from 'app/shared/filteredReleases/ensureEmptySwimlanes';
import {
  generateQuarters,
  sortReleasesByOrder
} from 'app/shared/filteredReleases/quarterUtils';
import {
  selectDateRange,
  selectFilteredReleases,
  selectHideEmptySwimLanes,
  selectLanesTiers,
  selectReleases
} from 'app/pages/Home/slice/selectors';

/**
 * Returns release lanes for the monthly view, mirroring quarterly `releasesToRender`:
 * - Uses Redux `filteredReleases` synced by `useSyncFilteredReleases`
 * - Keeps selected swimlanes visible even when empty
 * - Honors `hideEmptySwimLanes` (Must Win deep link) the same way quarterly does
 */
export function useMonthlySearchFilteredReleases(): any[] {
  const allReleases = useSelector(selectReleases);
  const filteredReleases = useSelector(selectFilteredReleases);
  const hideEmptySwimLanes = useSelector(selectHideEmptySwimLanes);
  const lanesTiers = useSelector(selectLanesTiers);
  const dateRange = useSelector(selectDateRange);

  const filterOptions = useMemo(() => {
    const options = [{ key: 'all', label: 'All' }];

    if (allReleases?.releaseScheduled) {
      const types = allReleases.releaseScheduled.map(
        (item: any) => item.type as string
      );
      const uniqueTypes: string[] = Array.from(new Set(types));

      uniqueTypes.forEach(type => {
        options.push({
          key: type.toLowerCase().replace(/\s+/g, '-'),
          label: type
        });
      });
    }

    return options;
  }, [allReleases?.releaseScheduled]);

  const quarters = useMemo(
    () => generateQuarters(dateRange.start, dateRange.end),
    [dateRange.end, dateRange.start]
  );

  return useMemo(() => {
    const selectedTypes = lanesTiers.selectedLanesTiers || [];
    const withEmptySwimlanes = ensureEmptySwimlanes(
      filteredReleases || [],
      filterOptions,
      selectedTypes,
      quarters
    );

    const releasesToRender = !hideEmptySwimLanes
      ? withEmptySwimlanes
      : withEmptySwimlanes.filter(releaseHasVisibleProjects);

    return sortReleasesByOrder(releasesToRender);
  }, [
    filteredReleases,
    filterOptions,
    hideEmptySwimLanes,
    lanesTiers.selectedLanesTiers,
    quarters
  ]);
}
